import { QueryClient, DefaultOptions } from '@tanstack/react-query';

/**
 * Production-optimized React Query configuration
 */

const queryConfig: DefaultOptions = {
  queries: {
    // Stale time: how long data is considered fresh
    staleTime: 5 * 60 * 1000, // 5 minutes
    
    // Cache time: how long unused data stays in cache
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    
    // Retry configuration
    retry: (failureCount, error: any) => {
      // Don't retry on 404s or auth errors
      if (error?.status === 404 || error?.code === 'PGRST116') {
        return false;
      }
      
      // Don't retry on client errors (4xx)
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      
      // Retry server errors up to 3 times
      return failureCount < 3;
    },
    
    // Exponential backoff for retries
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    
    // Don't refetch on window focus in production (reduces unnecessary API calls)
    refetchOnWindowFocus: import.meta.env.DEV,
    
    // Refetch on reconnect to get fresh data
    refetchOnReconnect: true,
    
    // Refetch on mount when the cached data is stale. We previously had this
    // set to `false`, which combined with the 5-minute `staleTime` meant any
    // page mount inside that window reused whatever was cached — including
    // empty arrays cached before auth had finished loading or data cached
    // under a previous user/role. That's what was forcing users to hard-
    // refresh after login. With `true` (React Query's default), fresh data
    // (within `staleTime`) is still served instantly from cache, but stale
    // data triggers a background refetch on mount instead of being silently
    // displayed.
    refetchOnMount: true,
    
    // Enable network mode optimizations
    networkMode: 'online',
  },
  mutations: {
    // Retry mutations on network failures
    retry: 1,
    
    // Network mode for mutations
    networkMode: 'online',
  },
};

/**
 * Create a production-optimized QueryClient instance
 */
export const createOptimizedQueryClient = () => {
  return new QueryClient({
    defaultOptions: queryConfig,
    
    // Query cache configuration for memory management
    queryCache: undefined, // Use default
    
    // Mutation cache configuration
    mutationCache: undefined, // Use default
  });
};

/**
 * App-wide React Query client singleton.
 *
 * Exported as a module-level instance so anything that lives OUTSIDE the
 * `<QueryClientProvider>` tree (e.g. `AuthProvider`, which wraps it in
 * `App.tsx`) can still call `.clear()` / `.invalidateQueries()` directly,
 * without needing `useQueryClient()` (which would throw because no
 * `QueryClientProvider` is in scope).
 *
 * IMPORTANT: `<QueryClientProvider>` in `App.tsx` MUST be passed this same
 * instance so the cache mutated here is the same one the React tree reads.
 */
export const appQueryClient = createOptimizedQueryClient();

/**
 * Prefetch commonly accessed queries for better UX
 */
export const prefetchCommonQueries = async (queryClient: QueryClient, userId?: string) => {
  // Prefetch user roles if user is logged in
  if (userId) {
    try {
      await queryClient.prefetchQuery({
        queryKey: ['userRoles', userId],
        staleTime: 10 * 60 * 1000, // 10 minutes
      });
    } catch (error) {
      console.error('Failed to prefetch user roles:', error);
    }
  }
};

/**
 * Reload the current page for a newly selected country.
 * Do not use queryClient.clear() here — that empties the cache without
 * refetching, so the open page stays blank until a hard refresh.
 */
export const refreshTenantQueries = async (queryClient: QueryClient = appQueryClient) => {
  await queryClient.cancelQueries();
  await queryClient.resetQueries();
};

/**
 * Clear all queries for a user on logout
 */
export const clearUserQueries = (queryClient: QueryClient, userId: string) => {
  // Remove queries that contain userId
  queryClient.removeQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return Array.isArray(key) && key.some(k => k === userId);
    },
  });
  
  // Clear the entire cache on logout for security
  queryClient.clear();
};
